'use client';

import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface PhotoGalleryModalProps {
  images: string[];
  isOpen: boolean;
  onClose: () => void;
  initialIndex?: number;
  activityTitle: string;
}

/**
 * Full-screen photo gallery modal component with manual implementation
 * Displays activity images with navigation and thumbnail strip
 */
export function PhotoGalleryModal({
  images,
  isOpen,
  onClose,
  initialIndex = 0,
  activityTitle
}: PhotoGalleryModalProps) {
  const t = useTranslations('ActivityDetail');
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [mounted, setMounted] = useState(false);

  // Handle mounting for SSR compatibility
  useEffect(() => {
    setMounted(true);
  }, []);

  // Reset current index when modal opens with new initial index
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  // Handle body scroll lock and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    // Lock body scroll
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      // Restore body scroll
      document.body.style.overflow = originalStyle;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, goToPrevious, goToNext]);

  const goToImage = (index: number) => {
    setCurrentIndex(index);
  };

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Don't render on server or when not mounted
  if (!mounted || !isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="photo-gallery-title"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-[600px] h-[520px] px-5 py-4 mx-auto flex flex-col animate-in fade-in-0 zoom-in-95 duration-300 bg-white rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 mb-6">
          <div className="flex items-center justify-between">
            <h4 id="photo-gallery-title" className="text-lg text-dark-300">
              {t('photoGallery')}
            </h4>
            <button onClick={onClose} className="rounded-lg" aria-label={t('photoGallery')}>
              <X className="w-6 h-6 text-neutral-700" />
            </button>
          </div>
        </div>

        {/* Main Image Area */}
        <div className="flex-1 relative  overflow-hidden">
          <div className="relative w-full h-full flex items-center justify-center">
            <Image
              src={images[currentIndex]}
              alt={`${activityTitle} - Image ${currentIndex + 1}`}
              fill
              sizes="(max-width: 768px) 100vw, 600px"
              className="object-contain rounded-2xl"
              priority
            />
          </div>
        </div>

        {/* Thumbnail Navigation */}
        {images.length > 1 && (
          <div className="flex-shrink-0 mt-2">
            <div className="relative">
              {/* Previous Arrow */}
              <button
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 bg-white -translate-y-1/2 z-10 p-1 border border-neutral-200 rounded-full shadow-lg"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-4 h-4 text-neutral-900" />
              </button>

              {/* Thumbnails */}
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => goToImage(index)}
                    className={`relative flex-shrink-0 w-25 h-25 rounded-lg overflow-hidden border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-200 ${
                      index === currentIndex ? 'border-primary-200 ring-2 ring-primary-200' : ''
                    }`}
                    aria-label={`View image ${index + 1}`}
                  >
                    <Image
                      src={image}
                      alt={`${activityTitle} - Thumbnail ${index + 1}`}
                      fill
                      sizes="100px"
                      className="object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Next Arrow */}
              <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 bg-white -translate-y-1/2 z-10 p-1 border border-neutral-200 rounded-full shadow-lg"
                aria-label="Next image"
              >
                <ChevronRight className="w-4 h-4 text-neutral-900" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
